import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    LuMusic,
    LuMapPin,
    LuPencil,
    LuHouse,
    LuTarget,
    LuSlidersHorizontal,
    LuSparkles,
    LuChevronRight,
    LuCheck,
    LuSearch,
    LuX,
} from 'react-icons/lu';

import { Mode, ClientDatabase, IClient } from '~/types/client';
import { Screen } from '~/types/screen';
import { logEvent } from '~/config/firebase.config';
import {
    schemaForGame,
    resolveOptions,
    initialGameParams,
    nextGameParams,
    optionLabel,
    isOptionNew,
    type ParamsMap,
    type ParamsPatch,
    type ParamField,
    type ChipsField,
    type SelectField,
    type ToggleField,
    type MultiselectField,
    type SelectOption,
    type TFunc,
} from '~/config/gameParamsSchema.config';

import type { AiPlaylistOption } from '~/config/blindTestParams.config';
import { changedParams, isMemorizedGame, savedGameParams } from '~/config/gameParamsMemory';
import { useAiPlaylists } from './useAiPlaylists';
import styles from './GameParamsPanel.module.css';

/** Textes propres au panneau (le reste vient du schéma : animator.params.*). */
const TXT = 'animator.params.panel';

/** Au-delà, une liste de choix reçoit un champ de recherche et défile dans son cadre. */
const SEARCH_THRESHOLD = 12;
/** Au-delà, la liste défile dans un cadre borné (la ligne du réglage reste visible). */
const BOUNDED_THRESHOLD = 8;

/**
 * Découpe une liste d'options en blocs consécutifs de même `groupKey` (Blind Test :
 * « Mes playlists » / « France » / « International »). Un bloc sans `groupKey` sort
 * sans en-tête.
 */
function groupOptions(options: SelectOption[]): { groupKey?: string; options: SelectOption[] }[] {
    return options.reduce<{ groupKey?: string; options: SelectOption[] }[]>((acc, option) => {
        const last = acc[acc.length - 1];
        if (last && last.groupKey === option.groupKey) last.options.push(option);
        else acc.push({ groupKey: option.groupKey, options: [option] });
        return acc;
    }, []);
}

/** Comparaison de recherche sans casse ni accents (« eme » trouve « Années 80 »… et « Œuvre »). */
function fold(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/œ/gi, 'oe').toLowerCase();
}

/**
 * Colonnes d'un sélecteur segmenté : autant que la longueur des libellés le permet,
 * puis équilibrées sur les lignes (9 paliers → 5 + 4, pas 6 + 3 ; 4 « Sans limite »
 * → 2 × 2).
 */
function segmentColumns(labels: string[]): number {
    const longest = labels.reduce((m, l) => Math.max(m, l.length), 0);
    let max = 2;
    if (longest <= 3) max = 6;
    else if (longest <= 5) max = 5;
    else if (longest <= 8) max = 4;
    else if (longest <= 12) max = 3;
    const rows = Math.ceil(labels.length / max);
    return Math.max(1, Math.ceil(labels.length / rows));
}

/** Premier ancêtre qui défile (le corps de la feuille), sinon la page. */
function scrollParentOf(el: HTMLElement): HTMLElement {
    let node = el.parentElement;
    while (node) {
        const { overflowY } = getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
        node = node.parentElement;
    }
    return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

/** Fait défiler le conteneur juste assez pour montrer [top, bottom] (le haut prime). */
function revealRange(top: HTMLElement, bottom: HTMLElement) {
    const sp = scrollParentOf(top);
    const spRect = sp === document.scrollingElement ? { top: 0 } : sp.getBoundingClientRect();
    const view = sp === document.scrollingElement ? window.innerHeight : sp.clientHeight;
    const y0 = top.getBoundingClientRect().top - spRect.top + sp.scrollTop - 8;
    const y1 = bottom.getBoundingClientRect().bottom - spRect.top + sp.scrollTop + 12;
    let target = sp.scrollTop;
    if (y1 > target + view) target = y1 - view;
    if (y0 < target) target = y0;
    if (Math.abs(target - sp.scrollTop) < 2) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sp.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
}

const ICONS = {
    music: LuMusic,
    'map-pin': LuMapPin,
    pencil: LuPencil,
    home: LuHouse,
    goal: LuTarget,
    sliders: LuSlidersHorizontal,
} as const;

/**
 * Animator Control — panneau de réglages GÉNÉRIQUE, piloté par le schéma du jeu
 * courant (GAME_PARAMS_SCHEMAS), affiché dans une feuille du cockpit.
 *
 * Les réglages se lisent d'abord comme la fiche de la partie : une ligne par
 * réglage, « Libellé ……… valeur › ». On touche une ligne, son choix s'ouvre dessous
 * (un seul à la fois) ; un interrupteur se règle sur la ligne même. Refonte
 * d'octobre 2026 (design lab : la « fiche » choisie parmi 4 propositions).
 *
 * Logique : état local initialisé depuis screen.gameParams (`initialGameParams`),
 * `apply()` qui re-valide les champs dépendants et écrit l'objet COMPLET
 * (`nextGameParams`), `toggleMulti` qui garde `minSelected`. Rien n'est écrit au
 * montage (gotchas #149/#184) : seulement sur un geste de l'animateur. Un réglage
 * facultatif (Blind Test : les deux modes) n'est écrit que s'il a été choisi ici ;
 * sinon sa ligne affiche « Comme sur la TV » et le jeu garde le choix de l'écran.
 */
export function GameParamsPanel({
    client,
    screen,
    live: providedLive,
    gameID,
    aiPlaylists: providedAiPlaylists,
}: {
    client: IClient;
    screen?: Screen;
    /** Présence de la TV calculée par le cockpit (qui mémorise les TV ancienne version). */
    live?: boolean;
    gameID: string;
    /**
     * Playlists IA déjà chargées par le parent. Le cockpit les charge dès son
     * ouverture : chargées seulement à l'ouverture de la feuille, un réglage touché
     * avant leur arrivée faisait re-valider la playlist IA choisie contre une liste
     * qui ne la contient pas encore → remplacée par la playlist par défaut.
     */
    aiPlaylists?: AiPlaylistOption[];
}) {
    const { t } = useTranslation();
    const ownAiPlaylists = useAiPlaylists(providedAiPlaylists ? '' : client.id, gameID);
    const aiPlaylists = providedAiPlaylists ?? ownAiPlaylists;
    // Langue de l'ÉTABLISSEMENT, pas celle d'i18next : `useState` ci-dessous fige
    // les valeurs initiales au premier rendu, avant que le cockpit n'ait basculé
    // i18next sur la langue du client — on figerait des défauts français.
    const schema = schemaForGame(gameID, aiPlaylists, client.language);
    const live = providedLive ?? screen?.isLive() ?? false;
    // Réglages enregistrés pour CE jeu, suivis en direct : ceux que la TV annonce
    // (Blind Test) comme ceux posés depuis ce téléphone. Un geste ici s'affiche tout de
    // suite (`pending`), puis la valeur enregistrée reprend la main dès qu'elle change
    // — y compris quand l'animateur règle quelque chose sur la TV entre-temps.
    const saved = savedGameParams(screen, gameID) as ParamsMap | undefined;
    const savedKey = JSON.stringify(saved ?? null);
    const [pending, setPending] = useState<{ base: string; params: ParamsMap } | null>(null);
    // Dès que l'enregistré change, le geste en attente est périmé : on l'efface (sinon
    // il réapparaîtrait si l'enregistré revenait plus tard à la même valeur).
    useEffect(() => {
        setPending((p) => (p && p.base !== savedKey ? null : p));
    }, [savedKey]);
    const fromSaved: ParamsMap = schema ? initialGameParams(schema, saved) : (saved ?? {});
    const params: ParamsMap = pending && pending.base === savedKey ? pending.params : fromSaved;

    // Ligne ouverte (une seule), dernière ligne modifiée (sa valeur « se pose »),
    // texte de recherche de la liste ouverte.
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [flashKey, setFlashKey] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const uid = useId();

    const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const regionRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    /** Ligne à rendre visible + focus après qu'un choix a refermé sa liste. */
    const returnToRef = useRef<string | null>(null);

    // Ouverture : la liste centre l'option courante dans son cadre, puis la feuille
    // défile juste assez pour montrer la ligne et son choix. Fermeture après un
    // choix : on revient sur la ligne (focus compris, l'option touchée a disparu).
    useEffect(() => {
        if (openKey) {
            const list = listRef.current;
            const current = list?.querySelector<HTMLElement>('[data-current="true"]');
            if (list && current && list.scrollHeight > list.clientHeight) {
                list.scrollTop = current.offsetTop - list.clientHeight / 2 + current.offsetHeight / 2;
            }
            const row = rowRefs.current[openKey];
            if (row && regionRef.current) revealRange(row, regionRef.current);
            return;
        }
        const back = returnToRef.current;
        returnToRef.current = null;
        const row = back ? rowRefs.current[back] : null;
        if (row) {
            row.focus({ preventScroll: true });
            revealRange(row, row);
        }
    }, [openKey]);

    if (!schema) return null;

    const Icon = ICONS[schema.icon] ?? LuSlidersHorizontal;

    // Effets (journal, écriture Firestore) HORS de l'updater de setState : React peut
    // rejouer un updater (gotcha #136) → écritures et événements en double.
    const apply = (patch: ParamsPatch) => {
        // Re-valide les champs dépendants ; les réglages facultatifs non choisis restent absents.
        const next = nextGameParams(schema, params, patch);
        const gesture = { base: savedKey, params: next };
        setPending(gesture);
        const [field, value] = Object.entries(patch)[0];
        logEvent('animator_set_param', client.id, {
            clientID: client.id,
            gameID,
            field,
            // null = remis sur « Comme sur la TV »
            value: Array.isArray(value) ? value.length : (value ?? 'tv'),
        });
        // Jeu mémorisé : seuls les réglages que CE geste a changés (une TV touchée
        // entre-temps garde les siens) ; 1re fois, tout.
        const hasMemory = !!screen?.gameParamsByGame?.[gameID];
        const write = isMemorizedGame(gameID)
            ? ClientDatabase.setMemorizedGameParams(
                  client.id,
                  Mode.Anim,
                  gameID,
                  next,
                  hasMemory ? changedParams(params, next) : next
              )
            : ClientDatabase.setGameParams(client.id, Mode.Anim, next);
        write.catch(() => {
            // L'écran garde ses réglages si l'écriture échoue : on réaffiche les vrais —
            // sauf si un geste plus récent a pris la place entre-temps.
            setPending((p) => (p === gesture ? null : p));
        });
    };

    const toggleMulti = (f: MultiselectField, value: string) => {
        const arr = (params[f.key] as string[]) ?? [];
        const has = arr.includes(value);
        if (has && arr.length <= f.minSelected) return; // garder au moins minSelected
        apply({ [f.key]: has ? arr.filter((v) => v !== value) : [...arr, value] });
    };

    /** Un geste de l'animateur sur un réglage : écrit + fait « se poser » la valeur. */
    const change = (key: string, patch: ParamsPatch) => {
        apply(patch);
        setFlashKey(key);
    };

    const toggleRow = (key: string) => {
        setQuery('');
        setOpenKey((k) => (k === key ? null : key));
    };

    const chipText = (f: ChipsField, n: number) => (f.format ? f.format(n, t as TFunc) : String(n));

    /** Valeur courante d'un champ, telle qu'elle s'affiche sur sa ligne. */
    const summaryOf = (f: ParamField): string => {
        const v = params[f.key];
        if (f.type === 'chips') return typeof v === 'number' ? chipText(f, v) : t(f.unsetLabelKey ?? `${TXT}.empty`);
        if (f.type === 'select') {
            const opt = resolveOptions(f, params).find((o) => o.value === v);
            // Choix gardé mais impossible ici (« L'œuvre » sur une playlist sans films) : on le dit.
            if (opt?.unavailable) return t(opt.unavailable.summaryKey);
            if (opt) return optionLabel(opt, t);
            return t(f.unsetLabelKey ?? `${TXT}.empty`);
        }
        if (f.type === 'toggle') return t(v === true ? f.labelOnKey : f.labelOffKey);
        const selected = Array.isArray(v) ? v.length : 0;
        return t(`${TXT}.countOf`, { n: selected, total: f.options.length });
    };

    /** Pastille sur la ligne repliée tant qu'une option nouvelle n'est pas celle choisie. */
    const hasNewChoice = (f: ParamField) =>
        f.type === 'select' && resolveOptions(f, params).some((o) => isOptionNew(o) && params[f.key] !== o.value);

    /* ---------- choix ouverts ---------- */

    const renderChips = (f: ChipsField) => {
        const opts = resolveOptions(f, params);
        const labels = opts.map((n) => chipText(f, n));
        return (
            <div
                className={styles.segments}
                role="radiogroup"
                aria-label={t(f.labelKey)}
                style={{ gridTemplateColumns: `repeat(${segmentColumns(labels)}, minmax(0, 1fr))` }}
            >
                {opts.map((n, i) => {
                    const on = params[f.key] === n;
                    return (
                        <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            className={`${styles.segment} ${on ? styles.lit : ''}`}
                            onClick={() => change(f.key, { [f.key]: n })}
                            disabled={!live}
                        >
                            {labels[i]}
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderSelect = (f: SelectField) => {
        const opts = resolveOptions(f, params);
        const searchable = opts.length > SEARCH_THRESHOLD;
        const bounded = opts.length > BOUNDED_THRESHOLD;
        const q = fold(query.trim());
        const shown = q ? opts.filter((o) => fold(optionLabel(o, t)).includes(q)) : opts;
        const pick = (value: string) => {
            if (value !== params[f.key]) change(f.key, { [f.key]: value });
            returnToRef.current = f.key;
            setOpenKey(null);
        };
        return (
            <>
                {searchable && (
                    <div className={styles.search}>
                        <LuSearch className={styles.searchIcon} aria-hidden="true" />
                        <input
                            type="search"
                            className={styles.searchInput}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={(e) => {
                                // Laisser le clavier monter, puis garder le champ au-dessus.
                                const el = e.currentTarget;
                                window.setTimeout(() => revealRange(el, el), 280);
                            }}
                            placeholder={t(`${TXT}.search`)}
                            aria-label={`${t(`${TXT}.search`)} · ${t(f.labelKey)}`}
                            enterKeyHint="search"
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                            disabled={!live}
                        />
                        {query && (
                            <button
                                type="button"
                                className={styles.searchClear}
                                onClick={() => setQuery('')}
                                aria-label={t(`${TXT}.clearSearch`)}
                            >
                                <LuX />
                            </button>
                        )}
                    </div>
                )}
                <div
                    ref={listRef}
                    className={`${styles.options} ${bounded ? styles.optionsBounded : ''}`}
                    role="radiogroup"
                    aria-label={t(f.labelKey)}
                >
                    {shown.length === 0 && <p className={styles.noResult}>{t(`${TXT}.noResult`)}</p>}
                    {groupOptions(shown).map(({ groupKey, options }, gi) => (
                        // eslint-disable-next-line react/no-array-index-key -- blocs consécutifs, l'ordre EST l'identité
                        <div key={`${groupKey ?? 'flat'}-${gi}`} className={styles.group}>
                            {groupKey && <p className={styles.groupHead}>{t(groupKey)}</p>}
                            {options.map((o) => {
                                const on = params[f.key] === o.value;
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={on}
                                        data-current={on}
                                        className={`${styles.option} ${on ? styles.optionOn : ''} ${
                                            o.unavailable ? styles.optionUnavailable : ''
                                        }`}
                                        onClick={() => pick(o.value)}
                                        disabled={!live || !!o.unavailable}
                                    >
                                        <span className={styles.optionLabel}>
                                            {optionLabel(o, t)}
                                            {isOptionNew(o) && (
                                                <span className={styles.newBadge}>{t(`${TXT}.new`)}</span>
                                            )}
                                            {o.unavailable && (
                                                <span className={styles.optionHint}>{t(o.unavailable.reasonKey)}</span>
                                            )}
                                        </span>
                                        <span className={styles.radio} aria-hidden="true">
                                            {on && <LuCheck />}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderMulti = (f: MultiselectField) => {
        const selected = (params[f.key] as string[]) ?? [];
        const all = f.options.map((o) => o.value);
        const atMin = selected.length <= f.minSelected;
        const allOn = all.every((v) => selected.includes(v));
        // « Aucun » garde le minimum exigé : les premières cases cochées, dans l'ordre de la liste.
        const keep = all.filter((v) => selected.includes(v)).slice(0, f.minSelected);
        return (
            <>
                <div className={styles.multiBar}>
                    <span className={styles.multiCount}>
                        {atMin ? t(`${TXT}.atLeast`, { n: f.minSelected }) : summaryOf(f)}
                    </span>
                    <div className={styles.quick}>
                        <button
                            type="button"
                            className={styles.quickBtn}
                            onClick={() => change(f.key, { [f.key]: all })}
                            disabled={!live || allOn}
                        >
                            {t(`${TXT}.all`)}
                        </button>
                        <button
                            type="button"
                            className={styles.quickBtn}
                            onClick={() => change(f.key, { [f.key]: keep })}
                            disabled={!live || atMin}
                        >
                            {t(`${TXT}.none`)}
                        </button>
                    </div>
                </div>
                <div className={styles.checks} role="group" aria-label={t(f.labelKey)}>
                    {f.options.map((o) => {
                        const on = selected.includes(o.value);
                        const locked = on && atMin;
                        return (
                            <button
                                key={o.value}
                                type="button"
                                role="checkbox"
                                aria-checked={on}
                                aria-disabled={locked || undefined}
                                className={`${styles.check} ${on ? styles.checkOn : ''} ${locked ? styles.checkLocked : ''}`}
                                onClick={() => {
                                    toggleMulti(f, o.value);
                                    setFlashKey(f.key);
                                }}
                                disabled={!live}
                            >
                                <span className={styles.box} aria-hidden="true">
                                    {on && <LuCheck />}
                                </span>
                                <span className={styles.checkLabel}>{optionLabel(o, t)}</span>
                            </button>
                        );
                    })}
                </div>
            </>
        );
    };

    /* ---------- lignes de la fiche ---------- */

    const renderToggleRow = (f: ToggleField) => {
        const on = params[f.key] === true;
        return (
            <li key={f.key} className={styles.item}>
                <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    className={styles.row}
                    onClick={() => change(f.key, { [f.key]: !on })}
                    disabled={!live}
                >
                    <span className={styles.rowLabel}>{t(f.labelKey)}</span>
                    <span
                        key={flashKey === f.key ? String(on) : 'v'}
                        className={`${styles.rowValue} ${styles.rowValueSoft} ${flashKey === f.key ? styles.flash : ''}`}
                    >
                        {summaryOf(f)}
                    </span>
                    <span className={`${styles.switch} ${on ? styles.switchOn : ''}`} aria-hidden="true">
                        <span className={styles.knob} />
                    </span>
                </button>
            </li>
        );
    };

    const renderRow = (f: ParamField) => {
        if (schema.hideField?.(f.key, params)) return null;
        if (f.type === 'toggle') return renderToggleRow(f);

        const open = live && openKey === f.key;
        const regionId = `${uid}-${f.key}`;
        const value = summaryOf(f);
        return (
            <li key={f.key} className={`${styles.item} ${open ? styles.itemOpen : ''}`}>
                <button
                    ref={(el) => {
                        rowRefs.current[f.key] = el;
                    }}
                    type="button"
                    className={styles.row}
                    aria-expanded={open}
                    aria-controls={open ? regionId : undefined}
                    onClick={() => toggleRow(f.key)}
                    disabled={!live}
                >
                    <span className={styles.rowLabel}>
                        {t(f.labelKey)}
                        {hasNewChoice(f) && <span className={styles.newBadge}>{t(`${TXT}.new`)}</span>}
                    </span>
                    <span
                        key={flashKey === f.key ? value : 'v'}
                        className={`${styles.rowValue} ${flashKey === f.key ? styles.flash : ''}`}
                    >
                        {value}
                    </span>
                    <LuChevronRight className={styles.chevron} aria-hidden="true" />
                </button>
                {open && (
                    <div id={regionId} ref={regionRef} className={styles.region}>
                        {f.type === 'chips' && renderChips(f)}
                        {f.type === 'select' && renderSelect(f)}
                        {f.type === 'multiselect' && renderMulti(f)}
                    </div>
                )}
            </li>
        );
    };

    return (
        <div className={styles.panel}>
            <header className={styles.head}>
                <span className={styles.headIcon} aria-hidden="true">
                    <Icon />
                </span>
                <h2 className={styles.title}>{t(schema.titleKey)}</h2>
            </header>

            {!live && (
                <p className={styles.offline}>
                    <span className={styles.offlineDot} aria-hidden="true" />
                    {t(`${TXT}.offline`)}
                </p>
            )}

            <ul className={`${styles.card} ${!live ? styles.cardOff : ''}`}>{schema.fields.map(renderRow)}</ul>

            {schema.note && (
                <div className={styles.note}>
                    <LuSparkles className={styles.noteIcon} aria-hidden="true" />
                    <p className={styles.noteText}>
                        <span className={styles.noteTitle}>{t(schema.note.titleKey)}</span>
                        <span className={styles.noteSub}>{t(schema.note.subKey)}</span>
                    </p>
                </div>
            )}
        </div>
    );
}
